

import { useState, useEffect } from "react"
import { Modal, Spinner, Alert, Badge } from "react-bootstrap"
import axios from "axios"

const QuoteTimelineModal = ({ show, onHide, quoteId, role = "buyer" }) => {
  const [timeline, setTimeline] = useState([])
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const authInfo = JSON.parse(localStorage.getItem("authInfo"))

  useEffect(() => {
    if (show && quoteId) {
      fetchTimeline()
    }
  }, [show, quoteId])

  const fetchTimeline = async () => {
    setLoading(true)
    setError("")

    try {
      const timelineEndpoint =
        role === "seller"
          ? `seller/quote/${quoteId}/timeline`
          : `user/quote/${quoteId}/timeline`

      const response = await axios.get(timelineEndpoint, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        setTimeline(response.data.data.timeline)
        setQuote(response.data.data.quote)
      } else {
        setError("Failed to load quote timeline")
      }
    } catch (error) {
      console.error("Error fetching timeline:", error)
      setError(error.response?.data?.message || "Failed to load quote timeline")
    } finally {
      setLoading(false)
    }
  }

  const getEventIcon = (event) => {
    const icons = {
      quote_created: "fas fa-plus-circle text-primary",
      quote_edited: "fas fa-edit text-warning",
      quote_responded: "fas fa-reply text-info",
      quote_finalized: "fas fa-lock text-secondary",
      quote_accepted: "fas fa-check-circle text-success",
      quote_rejected: "fas fa-times-circle text-danger",
    }
    return icons[event] || "fas fa-circle text-muted"
  }

  const getEventTitle = (event) => {
    const titles = {
      quote_created: "Quote Request Created",
      quote_edited: "Quote Request Edited",
      quote_responded: "Seller Response",
      quote_finalized: "Quote Finalized",
      quote_accepted: "Quote Accepted",
      quote_rejected: "Quote Rejected/Cancelled",
    }
    return titles[event] || event
  }

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Quote Timeline</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading timeline...</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {quote && !loading && (
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <img
                src={
                  quote.productId?.featuredImage ||
                  quote.serviceId?.featuredImage ||
                  "/placeholder.svg" ||
                  "/placeholder.svg"
                }
                alt="Item"
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  marginRight: "15px",
                  borderRadius: "8px",
                }}
              />
              <div>
                <h6 className="mb-1">{quote.productId?.name || quote.serviceId?.name}</h6>
                <p className="mb-1 text-muted">Quote ID: {quote.quoteCode}</p>
                <Badge
                  bg={quote.status === "accepted" ? "success" : quote.status === "rejected" ? "danger" : "warning"}
                >
                  {quote.status.toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {timeline.length > 0 && !loading && (
          <div className="timeline">
            {timeline.map((event, index) => (
              <div key={index} className="timeline-item d-flex mb-4">
                <div className="timeline-marker me-3">
                  <i className={getEventIcon(event.event)} style={{ fontSize: "1.2rem" }}></i>
                </div>
                <div className="timeline-content flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">{getEventTitle(event.event)}</h6>
                    <small className="text-muted">{formatTimestamp(event.timestamp)}</small>
                  </div>
                  <p className="mb-2 text-muted">
                    {event.description} by <strong>{event.actor}</strong>
                  </p>

                  {/* Event-specific data */}
                  {event.event === "quote_created" && event.data && (
                    <div className="bg-light p-3 rounded">
                      <p className="mb-2">
                        <strong>Message:</strong> {event.data.message}
                      </p>
                      {event.data.suggestedPrice && (
                        <p className="mb-2">
                          <strong>Suggested Price:</strong> ${event.data.suggestedPrice}
                        </p>
                      )}
                      {event.data.attachments && event.data.attachments.length > 0 && (
                        <p className="mb-0">
                          <strong>Attachments:</strong> {event.data.attachments.length} file(s)
                        </p>
                      )}
                    </div>
                  )}

                  {event.event === "quote_edited" && event.data && (
                    <div className="bg-warning bg-opacity-10 p-3 rounded">
                      <p className="mb-2">
                        <strong>Reason:</strong> {event.data.reason}
                      </p>
                      {event.data.previousPrice && event.data.newPrice && (
                        <p className="mb-2">
                          <strong>Price Change:</strong> ${event.data.previousPrice} → ${event.data.newPrice}
                        </p>
                      )}
                      {event.data.message && (
                        <p className="mb-0">
                          <strong>Updated Message:</strong> {event.data.message}
                        </p>
                      )}
                    </div>
                  )}

                  {event.event === "quote_responded" && event.data && (
                    <div className="bg-info bg-opacity-10 p-3 rounded">
                      <p className="mb-2">
                        <strong>Response:</strong> {event.data.message}
                      </p>
                      <p className="mb-2">
                        <strong>Quoted Price:</strong> ${event.data.price}
                      </p>
                      {event.data.deliveryTime && (
                        <p className="mb-2">
                          <strong>Delivery Time:</strong> {event.data.deliveryTime}
                        </p>
                      )}
                      {event.data.milestones && event.data.milestones.length > 0 && (
                        <div className="mt-2">
                          <p className="mb-1">
                            <strong>Milestones & Attached Items:</strong>
                          </p>
                          <div className="border rounded p-2 bg-white">
                            {event.data.milestones.map((milestone, idx) => (
                              <div key={idx} className={`mb-2 ${idx < event.data.milestones.length - 1 ? 'pb-2 border-bottom' : ''}`}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <strong>{milestone.title}</strong>
                                  <span className="text-success fw-bold">${milestone.amount}</span>
                                </div>
                                <small className="text-muted d-block">{milestone.description} ({milestone.estimatedDeliveryDays} days)</small>
                                {milestone.items && milestone.items.length > 0 && (
                                  <div className="mt-1 ps-2 border-start bg-light p-1 rounded">
                                    <small className="text-secondary fw-bold d-block mb-1">Attached Items:</small>
                                    {milestone.items.map((it, itemIdx) => (
                                      <div key={itemIdx} className="d-flex justify-content-between align-items-center small text-muted">
                                        <span>
                                          <i className={it.itemType === 'SERVICE' ? 'fas fa-wrench me-1 text-info' : 'fas fa-box me-1 text-primary'}></i>
                                          {it.name || 'Catalog Item'} {it.isOptional ? <span className="badge bg-warning text-dark ms-1">Optional</span> : ''}
                                        </span>
                                        <span>{it.quantity || 1} × ${it.unitPrice || 0}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {event.data.isFinal && (
                        <Badge bg="secondary" className="mt-2">
                          Final Quote
                        </Badge>
                      )}
                    </div>
                  )}

                  {event.event === "quote_accepted" && event.data && (
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <p className="mb-2">Quote has been accepted and converted to a project.</p>
                      {event.data.projectId && (
                        <p className="mb-0">
                          <strong>Project ID:</strong> {event.data.projectId}
                        </p>
                      )}
                    </div>
                  )}

                  {event.event === "quote_rejected" && event.data && (
                    <div className="bg-danger bg-opacity-10 p-3 rounded">
                      {event.data.reason && (
                        <p className="mb-0">
                          <strong>Reason:</strong> {event.data.reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {timeline.length === 0 && !loading && !error && (
          <div className="text-center py-4">
            <p className="text-muted">No timeline events found.</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default QuoteTimelineModal